import { TableCell, TableRow } from '@optools/components/ui/table';
import {
  MediaFeedType,
  PipelineStatus,
  ProgramSource,
  RegionalMediaPipeline,
  SourceType,
  StreamState,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { determineGameStateFromValues, GAME_STATE } from '@optools/utils/game-state';

import { useBroadcastSourceUpdate } from '../../hooks/useBroadcastSourceUpdate';
import { useProgramSourceSwitch } from '../../hooks/useProgramSourceSwitch';
import { BroadcastSourceSwitch } from './broadcast-source-switch';

const { ERROR, PROVISIONED, RUNNING, WARNING } = PipelineStatus;

export interface RegionalBroadcastSourceControlsProps {
  date: string;
  enableProgramSource: boolean;
  feedType: MediaFeedType;
  gamePk: number;
  isRefreshing?: boolean;
  mediaId: string;
  pipeline: RegionalMediaPipeline;
  streamState: StreamState;
}

export function RegionalBroadcastSourceControls({
  date,
  enableProgramSource,
  feedType,
  gamePk,
  isRefreshing,
  mediaId,
  pipeline,
  streamState,
}: RegionalBroadcastSourceControlsProps) {
  const {
    activeSlateCode,
    activeSource,
    gameSource,
    id,
    postgameSource,
    pregameSource,
    region,
    status,
  } = pipeline;

  const { handleBroadcastSourceChange, isBroadcastSourcePending } = useBroadcastSourceUpdate({
    date,
    feedType,
    gamePk,
    pipelineId: id,
    programSource: activeSource ?? ProgramSource.FRONTHAUL,
  });

  const { handleSwitchProgramSource, isProgramSourcePending } = useProgramSourceSwitch({
    date,
    gamePk,
    mediaId,
    pipelineId: id,
    programSource: activeSource ?? ProgramSource.FRONTHAUL,
  });

  const disabled = status == null || ![RUNNING, WARNING, PROVISIONED, ERROR].includes(status);

  const gameState = determineGameStateFromValues(activeSlateCode, streamState);

  const loading = isBroadcastSourcePending || isProgramSourcePending || !!isRefreshing;

  return (
    <TableRow className="divide-x">
      <TableCell className="font-semibold">{region}</TableCell>
      {enableProgramSource && (
        <TableCell data-testid="program-source">
          <BroadcastSourceSwitch
            active={true}
            broadcastSourceType="PROGRAM"
            broadcastSourceValue={activeSource ?? ProgramSource.FRONTHAUL}
            disabled={disabled}
            hideHeader
            loading={loading}
            region={region}
            onProgramSourceChange={handleSwitchProgramSource}
          />
        </TableCell>
      )}
      <TableCell data-testid="pre-game">
        <BroadcastSourceSwitch
          active={gameState === GAME_STATE.PRE_GAME}
          broadcastSourceType={SourceType.PREGAME}
          broadcastSourceValue={pregameSource ?? ProgramSource.FRONTHAUL}
          disabled={disabled}
          hideHeader
          loading={loading}
          region={region}
          onBroadcastSourceChange={handleBroadcastSourceChange}
        />
      </TableCell>
      <TableCell data-testid="game">
        <BroadcastSourceSwitch
          active={gameState === GAME_STATE.GAME}
          broadcastSourceType={SourceType.GAME}
          broadcastSourceValue={gameSource ?? ProgramSource.FRONTHAUL}
          disabled={disabled}
          hideHeader
          loading={loading}
          region={region}
          onBroadcastSourceChange={handleBroadcastSourceChange}
        />
      </TableCell>
      <TableCell data-testid="post-game">
        <BroadcastSourceSwitch
          active={gameState === GAME_STATE.POST_GAME}
          broadcastSourceType={SourceType.POSTGAME}
          broadcastSourceValue={postgameSource ?? ProgramSource.FRONTHAUL}
          disabled={disabled}
          hideHeader
          loading={loading}
          region={region}
          onBroadcastSourceChange={handleBroadcastSourceChange}
        />
      </TableCell>
    </TableRow>
  );
}
